/**
 * Level → Tailwind class lookup for level pills, borders, and row tints.
 * Known levels use semantic colors; unknown levels (custom log levels from
 * application code) are hash-assigned one of a small rotation palette so
 * they're still visually distinct.
 */
interface LevelStyle {
  pill: string;
  border: string;
  rowBg: string;
  text: string;
  letter: string;
}

const KNOWN: Record<string, LevelStyle> = {
  ERROR: {
    pill: "bg-red-500/20 text-red-400 border-red-500/40",
    border: "border-l-red-500",
    rowBg: "bg-red-500/[0.03]",
    text: "text-red-400",
    letter: "E",
  },
  FATAL: {
    pill: "bg-red-600/30 text-red-300 border-red-600/50",
    border: "border-l-red-600",
    rowBg: "bg-red-600/[0.05]",
    text: "text-red-300",
    letter: "F",
  },
  CRITICAL: {
    pill: "bg-red-600/30 text-red-300 border-red-600/50",
    border: "border-l-red-600",
    rowBg: "bg-red-600/[0.05]",
    text: "text-red-300",
    letter: "C",
  },
  WARN: {
    pill: "bg-yellow-500/20 text-yellow-400 border-yellow-500/40",
    border: "border-l-yellow-500",
    rowBg: "bg-yellow-500/[0.03]",
    text: "text-yellow-400",
    letter: "W",
  },
  WARNING: {
    pill: "bg-yellow-500/20 text-yellow-400 border-yellow-500/40",
    border: "border-l-yellow-500",
    rowBg: "bg-yellow-500/[0.03]",
    text: "text-yellow-400",
    letter: "W",
  },
  INFO: {
    pill: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    border: "border-l-emerald-500/70",
    rowBg: "",
    text: "text-emerald-400",
    letter: "I",
  },
  DEBUG: {
    pill: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    border: "border-l-blue-500/60",
    rowBg: "",
    text: "text-blue-400",
    letter: "D",
  },
  TRACE: {
    pill: "bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/30",
    border: "border-l-fuchsia-500/60",
    rowBg: "",
    text: "text-fuchsia-400",
    letter: "T",
  },
};

const CUSTOM_PALETTE: LevelStyle[] = [
  {
    pill: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
    border: "border-l-cyan-500/60",
    rowBg: "",
    text: "text-cyan-400",
    letter: "·",
  },
  {
    pill: "bg-orange-500/15 text-orange-400 border-orange-500/30",
    border: "border-l-orange-500/60",
    rowBg: "",
    text: "text-orange-400",
    letter: "·",
  },
  {
    pill: "bg-pink-500/15 text-pink-400 border-pink-500/30",
    border: "border-l-pink-500/60",
    rowBg: "",
    text: "text-pink-400",
    letter: "·",
  },
  {
    pill: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
    border: "border-l-indigo-500/60",
    rowBg: "",
    text: "text-indigo-400",
    letter: "·",
  },
  {
    pill: "bg-teal-500/15 text-teal-400 border-teal-500/30",
    border: "border-l-teal-500/60",
    rowBg: "",
    text: "text-teal-400",
    letter: "·",
  },
];

const customAssignments = new Map<string, LevelStyle>();

export function getLevelStyle(rawLevel: string): LevelStyle {
  const level = (rawLevel || "INFO").toUpperCase();
  const known = KNOWN[level];
  if (known) return known;
  let assigned = customAssignments.get(level);
  if (!assigned) {
    assigned = CUSTOM_PALETTE[customAssignments.size % CUSTOM_PALETTE.length]!;
    customAssignments.set(level, assigned);
  }
  return assigned;
}

/** Level severity rank for client-side sorting/filtering. Higher = more severe. */
export function levelRank(rawLevel: string): number {
  switch ((rawLevel || "").toUpperCase()) {
    case "FATAL":
    case "CRITICAL":
      return 5;
    case "ERROR":
      return 4;
    case "WARN":
    case "WARNING":
      return 3;
    case "INFO":
      return 2;
    case "DEBUG":
      return 1;
    case "TRACE":
      return 0;
    default:
      return 2;
  }
}

/** "vibe-fb-api-65867c8546-4w2b5" → "api-4w2b5" */
export function shortenPod(pod: string): string {
  if (!pod) return "-";
  const parts = pod.split("-");
  if (parts.length <= 2) return pod;
  const suffix = parts[parts.length - 1]!;
  const hashLen = parts[parts.length - 2]?.length ?? 0;
  if (hashLen >= 8) {
    const nameEnd = parts.length - 2;
    const meaningful = parts.slice(Math.max(0, nameEnd - 1), nameEnd).join("-");
    return `${meaningful}-${suffix}`;
  }
  return parts.slice(-2).join("-");
}

/** HH:mm:ss.SSS in local TZ. */
export function formatLogTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--:--:--";
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return `${h}:${m}:${s}.${ms}`;
}

export function formatLogTimeAbsolute(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    fractionalSecondDigits: 3,
  });
}
